import React, { useState } from "react";
import { useAuth } from "./hooks/AuthenticationProvider";
import { getFunctions, httpsCallable } from "firebase/functions";

const StripeOnboarding = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user, isAuthenticated, organizationId } = useAuth();

  const handleConnectExistingAccount = async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const connectExisting = httpsCallable(
        functions,
        "connectExistingStripeAccount"
      );

      const result = await connectExisting({
        email: user.email,
        organizationId: organizationId,
      });

      if (result.data.oauthUrl) {
        window.location.href = result.data.oauthUrl;
      } else {
        setError("Failed to create connection URL");
      }
    } catch (error) {
      console.error("Error connecting existing account:", error);
      setError(error.message || "Failed to connect existing account");
    } finally {
      setLoading(false);
    }
  };

  const handleSetupStripe = async () => {
    if (!isAuthenticated || !user) {
      setError("You must be logged in to set up Stripe payments");
      return;
    }

    if (!organizationId) {
      setError("Organization not found. Please try refreshing the page.");
      return;
    }

    console.log("Current user:", user); // Debug log
    console.log("User email:", user.email); // Debug log
    console.log("Organization ID:", organizationId); // Debug log

    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const createStripeAccount = httpsCallable(
        functions,
        "createStripeAccount"
      );

      const requestData = {
        email: user.email,
        organizationId: organizationId,
      };

      console.log("Sending data to function:", requestData); // Debug log

      const result = await createStripeAccount(requestData);

      console.log("Function result:", result); // Debug log

      if (result.data.onboardingUrl) {
        window.location.href = result.data.onboardingUrl;
      } else {
        setError("Failed to create onboarding URL");
      }
    } catch (error) {
      console.error("Error setting up Stripe:", error);
      setError(error.message || "Failed to setup Stripe account");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state if not authenticated yet or checking status
  if (!isAuthenticated || !user || !organizationId || stripeStatus === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Loading...
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Checking Stripe account status
          </p>
        </div>
      </div>
    );
  }

  // Show success state if Stripe is already set up
  if (stripeStatus === "complete") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900">
                Stripe Account Connected
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Your Stripe account is set up and ready to accept payments.
              </p>
              {accountId && (
                <p className="mt-1 text-xs text-gray-500">
                  Account ID: {accountId}
                </p>
              )}

              <div className="mt-6 space-y-3">
                <a
                  href="https://dashboard.stripe.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Stripe Dashboard
                </a>

                <button
                  onClick={checkStripeStatus}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Refresh Status
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          Setup Stripe Payments
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Connect your Stripe account to start accepting payments
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-4">
                You'll be redirected to Stripe to complete your account setup.
                This includes:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>Business information</li>
                <li>Bank account details</li>
                <li>Identity verification</li>
              </ul>
            </div>

            <button
              onClick={handleSetupStripe}
              disabled={loading}
              className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              }`}
            >
              {loading ? "Setting up..." : "Setup Stripe Account"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeOnboarding;
